import { useState, useEffect } from "react";
import { Navbar } from "./src/Navbar";
import { Sidebar } from "./src/Sidebar";
import { LoginPage } from "./src/LoginPage";
import { Dashboard } from "./src/Dashboard";
import { Toaster } from "./src/ui/sonner";
import { Footer } from "./src/Footer";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { PATHS } from "./src/config/paths";
import { ENDPOINTS, API_BASE_URL } from "./src/config/endpoints";
import { RoleGuard } from "./src/utils/RoleGuard";
import { PermissionProvider } from "./src/contexts/PermissionContext";

// Student
import { Profile } from "./src/student/Profile";
import { Timetable } from "./src/student/Timetable";
import { Attendance } from "./src/student/Attendance";
import { Examination } from "./src/student/Examination";
import { CourseRegistration } from "./src/student/CourseRegistration";
import { Library as StudentLibrary } from "./src/student/Library";
import { Fees } from "./src/student/Fees";
import { Hostel } from "./src/student/Hostel";
import { Transport } from "./src/student/Transport";

// Teacher
import { Classes } from "./src/teacher/Classes";
import { Salary } from "./src/teacher/Salary";
import { Leave } from "./src/teacher/Leave";
import MarkAttendance from "./src/teacher/Attendances";

// Admin
import { AdminDashboard } from "./src/admin/AdminDashboard";
import { UserManagement } from "./src/admin/UserManagement";
import { LeadTracking } from "./src/admin/LeadTracking";
import { LeadActivities } from "./src/admin/LeadActivities";
import { LeadAccounts } from "./src/admin/LeadAccounts";
import { LeadContact } from "./src/admin/LeadContact";
import { LeadLost } from "./src/admin/LeadLost";
import { LeadAll } from "./src/admin/LeadAll";
import { CampaignDashboard } from "./src/admin/CampaignDashboard";

// Superadmin
import { SuperadminDashboard } from "./src/SuperadminDashboard";
import { AccessControlCenter } from "./src/superadmin/AccessControlCenter";
import { Courses as CoursesData } from "./src/superadmin/Courses";
import { EditProfile } from "./src/superadmin/EditProfile";
import { ManageClient } from "./src/superadmin/ManageClient";
import { AddClient } from "./src/superadmin/AddClient";
import AddCustomerPage from "./src/superadmin/AddCustomer";
import AddCoursePage from "./src/superadmin/AddCourse";
import { ManageCustomer } from "./src/superadmin/ManageCustomer";
import { ViewProfile } from "./src/superadmin/ViewProfile";
import { ViewBooks } from "./src/superadmin/ViewBooks";
import { AddBook } from "./src/superadmin/AddBook";
import { Library as SuperadminLibrary } from "./src/superadmin/superadminLibrary"; // Keeping for reference if needed, but routes will use new ones
import { LeaveManagement } from "./src/superadmin/LeaveManagement";
import { ManageAttendance } from "./src/superadmin/ManageAttendance";
import { FeesStructure } from "./src/superadmin/FeesStructure";
import { SalaryPayout } from "./src/superadmin/SalaryPayout";
import AutoDialer from "./src/superadmin/AutoDialer";
import AddLeave from "./src/superadmin/addleave";
import SocialLeads from "./src/superadmin/SocialLeads";
import SocialDashboard from "./src/superadmin/SocialDashboard";
import LeadManagerTable from "./src/superadmin/LeadManagerTable";
import SocialKanban from "./src/superadmin/SocialKanban";
import SocialCampaigns from "./src/superadmin/SocialCampaigns";
import SocialIntegrations from "./src/superadmin/SocialIntegrations";
import { Notifications } from "./src/superadmin/Notifications";
import { SecurityPortal } from "./src/superadmin/SecurityPortal";
import { ForgotPasswordPage } from "./src/ForgotPasswordPage";


import { toast } from "sonner";
import { Reports } from "./src/admin/Reports";

interface User {
  id: number;
  username: string;
  name: string;
  email: string;
  role: "Superadmin" | "Admin" | "Teacher" | "Student" | "Client" | "Customer";
  token: string;
  refresh_token: string;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    if (savedTheme) return savedTheme;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);

  // 🌗 Theme logic
  const handleSetTheme = (newTheme: "light" | "dark") => {
    // @ts-ignore - document.startViewTransition is 
    if (!document.startViewTransition) {
      setTheme(newTheme);
      return;
    }
    // @ts-ignore
    document.startViewTransition(() => {
      setTheme(newTheme);
      // ✅ Toggle class synchronously so the View Transition captures the new theme in its snapshot
      document.documentElement.classList.toggle("dark", newTheme === "dark");
    });
  };

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // 🔐 Restore session
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("user");
      if (savedUser && savedUser !== "undefined") {
        setCurrentUser(JSON.parse(savedUser));
      }
    } catch (e) {
      console.error("Failed to restore user:", e);
      localStorage.removeItem("user");
    } finally {
      setLoadingUser(false);
    }
  }, []);

  const handleLogin = async ({ username, password }: any) => {
    const res = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.LOGIN}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.detail || "Login failed");
    }

    const user: User = {
      id: data.user.id,
      username: data.user.username,
      name: data.user.name || data.user.username,
      email: data.user.email || data.user.username,
      role:
        data.user.role === "superadmin"
          ? "Superadmin"
          : data.user.role === "admin"
            ? "Admin"
            : data.user.role === "teacher"
              ? "Teacher"
              : data.user.role === "client"
                ? "Client"
                : data.user.role === "customer"
                  ? "Customer"
                  : "Student",
      token: data.access_token,
      refresh_token: data.refresh_token,
    };

    // toast.success(`Welcome ${user.username}`); // Removed success toast on user request
    setTimeout(() => {
      localStorage.setItem("user", JSON.stringify(user));
      setCurrentUser(user);
    }, 2200);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setCurrentUser(null);
    navigate(PATHS.LOGIN);
    setIsSidebarOpen(false);
  };
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Guard Clauses for TypeScript
  if (loadingUser) return <div className="h-screen flex items-center justify-center">Loading…</div>;

  if (!currentUser) {
    return (
      <Routes>
        <Route path={PATHS.LOGIN} element={<LoginPage onLogin={handleLogin} />} />
        <Route path={PATHS.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path="*" element={<Navigate to={PATHS.LOGIN} replace />} />
      </Routes>
    );
  }

  const userProps = { id: String(currentUser.id), name: currentUser.name, email: currentUser.email, role: currentUser.role };

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden text-foreground">
      <Navbar
        theme={theme}
        setTheme={handleSetTheme}
        onMenuClick={toggleSidebar}
        currentUser={{ email: currentUser.email, role: currentUser.role }}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex overflow-hidden">
        <PermissionProvider userRole={currentUser.role}>
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
            userRole={currentUser.role}
          />

          <main className="flex-1 overflow-y-auto bg-background p-4 md:p-6 flex flex-col justify-between">
            <div key={location.pathname} className="page-enter">
              <Routes>
                {/* Common Routes */}
                <Route path={PATHS.DASHBOARD} element={<Dashboard user={currentUser} />} />
                <Route path={PATHS.VIEW_PROFILE} element={<ViewProfile user={userProps} />} />
                <Route path={PATHS.EDIT_PROFILE} element={<EditProfile user={userProps} />} />
                {/* Shared & Protected by Slugs */}
                <Route path={PATHS.USER_MANAGEMENT} element={<RoleGuard slug="users" currentUser={currentUser}><UserManagement /></RoleGuard>} />
                <Route path={PATHS.CLIENT_MANAGEMENT} element={<RoleGuard slug="manage-client" currentUser={currentUser}><ManageClient /></RoleGuard>} />
                <Route path={PATHS.ADD_CLIENT} element={<RoleGuard slug="manage-client" currentUser={currentUser}><AddClient /></RoleGuard>} />
                <Route path={PATHS.CUSTOMER_MANAGEMENT} element={<RoleGuard slug="manage-customer" currentUser={currentUser}><ManageCustomer /></RoleGuard>} />
                <Route path={PATHS.ADD_CUSTOMER} element={<RoleGuard slug="manage-customer" currentUser={currentUser}><AddCustomerPage /></RoleGuard>} />
                <Route path={PATHS.ACCESS_CONTROL} element={<RoleGuard allowedRoles={["Superadmin"]} currentUser={currentUser}><AccessControlCenter /></RoleGuard>} />
                <Route path={PATHS.SECURITY_PORTAL} element={<RoleGuard allowedRoles={["Superadmin"]} currentUser={currentUser}><SecurityPortal initialTab="sessions" /></RoleGuard>} />
                <Route path={PATHS.SECURITY_SESSIONS} element={<RoleGuard allowedRoles={["Superadmin"]} currentUser={currentUser}><SecurityPortal initialTab="sessions" /></RoleGuard>} />
                <Route path={PATHS.SECURITY_AUDIT} element={<RoleGuard allowedRoles={["Superadmin"]} currentUser={currentUser}><SecurityPortal initialTab="audit" /></RoleGuard>} />
                <Route path={PATHS.SECURITY_PROTECTION} element={<RoleGuard allowedRoles={["Superadmin"]} currentUser={currentUser}><SecurityPortal initialTab="settings" /></RoleGuard>} />

                <Route path={PATHS.COURSE_MANAGEMENT} element={<RoleGuard slug="courses" currentUser={currentUser}><CoursesData /></RoleGuard>} />
                <Route path={PATHS.ADD_COURSE} element={<RoleGuard slug="courses" currentUser={currentUser}><AddCoursePage /></RoleGuard>} />
                <Route path={PATHS.LEAVE_MANAGEMENT} element={<RoleGuard slug="leaves" currentUser={currentUser}><LeaveManagement /></RoleGuard>} />
                <Route path={PATHS.ADD_LEAVE} element={<RoleGuard slug="leaves" currentUser={currentUser}><AddLeave /></RoleGuard>} />
                <Route path={PATHS.AUTO_DIALER} element={<RoleGuard slug="auto-dialer" currentUser={currentUser}><AutoDialer /></RoleGuard>} />
                <Route path={PATHS.VIEW_LIBRARY} element={<RoleGuard slug="view-library" currentUser={currentUser}><ViewBooks /></RoleGuard>} />
                <Route path={PATHS.ADD_LIBRARY} element={<RoleGuard slug="add-library" currentUser={currentUser}><AddBook /></RoleGuard>} />
                {/* Leads */}
                <Route path={PATHS.CAMPAIGN_DASHBOARD} element={<RoleGuard slug="social-dashboard" currentUser={currentUser}><CampaignDashboard /></RoleGuard>} />
                <Route path={PATHS.LEAD_TRACKING} element={<RoleGuard slug="social-leads" currentUser={currentUser}><LeadTracking /></RoleGuard>} />
                <Route path={PATHS.LEAD_ACTIVITIES} element={<RoleGuard slug="lead-activities" currentUser={currentUser}><LeadActivities /></RoleGuard>} />
                <Route path={PATHS.LEAD_ACCOUNTS} element={<RoleGuard slug="lead-accounts" currentUser={currentUser}><LeadAccounts /></RoleGuard>} />
                <Route path={PATHS.LEAD_CONTACT} element={<RoleGuard slug="lead-contact" currentUser={currentUser}><LeadContact /></RoleGuard>} />
                <Route path={PATHS.LEAD_LOST} element={<RoleGuard slug="lead-lost" currentUser={currentUser}><LeadLost /></RoleGuard>} />
                <Route path={PATHS.LEAD_ALL} element={<RoleGuard slug="lead-all" currentUser={currentUser}><LeadAll /></RoleGuard>} />
                {/* Social */}
                <Route path={PATHS.SOCIAL_DASHBOARD} element={<RoleGuard slug="social-dashboard" currentUser={currentUser}><SocialDashboard /></RoleGuard>} />
                <Route path={PATHS.SOCIAL_LEADS} element={<RoleGuard slug="social-leads" currentUser={currentUser}><LeadManagerTable /></RoleGuard>} />
                <Route path={PATHS.SOCIAL_KANBAN} element={<RoleGuard slug="social-kanban" currentUser={currentUser}><SocialKanban /></RoleGuard>} />
                <Route path={PATHS.SOCIAL_CAMPAIGNS} element={<RoleGuard slug="social-campaigns" currentUser={currentUser}><SocialCampaigns /></RoleGuard>} />
                <Route path={PATHS.SOCIAL_INTEGRATIONS} element={<RoleGuard slug="social-integrations" currentUser={currentUser}><SocialIntegrations /></RoleGuard>} />
                {/* Payroll */}
                <Route path={PATHS.SALARY_PAYOUT} element={<RoleGuard slug="salary-payout" currentUser={currentUser}><SalaryPayout initialTab="runs" /></RoleGuard>} />
                <Route path={PATHS.PAYROLL_RUN} element={<RoleGuard slug="payroll-run" currentUser={currentUser}><SalaryPayout initialTab="runs" /></RoleGuard>} />
                <Route path={PATHS.SALARY_ASSIGNMENT} element={<RoleGuard slug="salary-assignment" currentUser={currentUser}><SalaryPayout initialTab="assign" /></RoleGuard>} />
                <Route path={PATHS.SALARY_CONFIGURATION} element={<RoleGuard slug="salary-configuration" currentUser={currentUser}><SalaryPayout initialTab="config" /></RoleGuard>} />
                {/* Operations */}
                <Route path={PATHS.FEES_STRUCTURE} element={<RoleGuard slug="fees" currentUser={currentUser}><FeesStructure /></RoleGuard>} />
                <Route path={PATHS.MANAGE_ATTENDANCE} element={<RoleGuard slug="manage-attendance" currentUser={currentUser}><ManageAttendance /></RoleGuard>} />
                <Route path={PATHS.VIEW_ATTENDANCE} element={<RoleGuard slug="view-attendance" currentUser={currentUser}><ManageAttendance /></RoleGuard>} />
                <Route path={PATHS.MARK_ATTENDANCE} element={<RoleGuard slug="mark-attendance" currentUser={currentUser}><MarkAttendance /></RoleGuard>} />
                <Route path={PATHS.REPORTS} element={<RoleGuard slug="reports" currentUser={currentUser}><Reports /></RoleGuard>} />
                <Route path={PATHS.NOTIFICATIONS} element={<Notifications />} />
                {/* Student specific modules made versatile */}
                <Route path={PATHS.STUDENT_PROFILE} element={<RoleGuard slug="profile" currentUser={currentUser}><Profile user={{ ...currentUser!, id: currentUser?.id.toString() }} /></RoleGuard>} />
                <Route path={PATHS.STUDENT_TIMETABLE} element={<RoleGuard slug="profile" currentUser={currentUser}><Timetable /></RoleGuard>} />
                <Route path={PATHS.STUDENT_FEES} element={<RoleGuard slug="fees" currentUser={currentUser}><Fees /></RoleGuard>} />
                <Route path={PATHS.STUDENT_ATTENDANCE} element={<RoleGuard slug="manage-attendance" currentUser={currentUser}><Attendance /></RoleGuard>} />
                <Route path={PATHS.STUDENT_EXAMINATION} element={<RoleGuard slug="examination" currentUser={currentUser}><Examination /></RoleGuard>} />
                <Route path={PATHS.STUDENT_LIBRARY} element={<RoleGuard slug="library" currentUser={currentUser}><StudentLibrary /></RoleGuard>} />
                <Route path={PATHS.STUDENT_COURSE_REG} element={<RoleGuard slug="courses" currentUser={currentUser}><CourseRegistration /></RoleGuard>} />
                {/* ... and so on ... */}

                <Route path="*" element={<Navigate to={PATHS.DASHBOARD} replace />} />
              </Routes>
            </div>
            <Footer />
          </main>
        </PermissionProvider>
      </div>

      <Toaster position="top-right" visibleToasts={6} richColors expand={true} closeButton />
    </div>
  );
}

